import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  FormContainer,
  NextButton,
  Prompt,
  QuestionContainer,
  QuestionPageContainer,
  RadioInput,
  StringInput,
} from "~/OnboardingApp/components";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { requiredString } from "~/utils/validations";

export type HasHealthConditions = "" | "Yes" | "No";

type Values = {
  hasChronicConditions: HasHealthConditions;
  chronicConditions: string;
};

const values: HasHealthConditions[] = ["Yes", "No"];

const validationSchema = Yup.object({
  hasChronicConditions: requiredString.oneOf(values),
  chronicConditions: Yup.string(),
});

export const HealthConditionsPage: FC = () => {
  const { t } = useTranslation();
  const { hasChronicConditions, chronicConditions } = useOnboarding();
  const { onSubmit } = useSubmitOnboardingQuestion();

  const initialValues: Values = {
    hasChronicConditions: hasChronicConditions || "",
    chronicConditions: chronicConditions || "",
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    validateOnMount: true,
  });

  const showAdditionalQuestion = formik.values.hasChronicConditions === "Yes";
  const isAutoAdvance = (option: string) => option === "No";
  const showButton = showAdditionalQuestion || !!hasChronicConditions;

  return (
    <QuestionPageContainer>
      <FormContainer formik={formik}>
        <QuestionContainer>
          <Prompt question={t("onboarding.healthConditionsPage.question")} />

          <RadioInput
            autoAdvance={isAutoAdvance}
            id="hasChronicConditions"
            values={values}
            defaultValue={hasChronicConditions}
          />
        </QuestionContainer>

        {showAdditionalQuestion && (
          <QuestionContainer my={5}>
            <Prompt
              question={t(
                "onboarding.healthConditionsPage.additionalQuestion.question",
              )}
            />

            <StringInput
              id="chronicConditions"
              placeholder={t(
                "onboarding.healthConditionsPage.additionalQuestion.placeholder",
              )}
            />
          </QuestionContainer>
        )}

        <NextButton showButton={showButton} />
      </FormContainer>
    </QuestionPageContainer>
  );
};
